import * as React from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaskSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { openTasksOf } from '@/lib/my-tasks';
import { formatDuration } from '@/lib/duration';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const workLogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work-log',
  component: WorkLogPage,
});

/** Ticks once a minute so the counter moves without re-fetching the session. */
function useMinuteTick(active: boolean): number {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, [active]);
  return tick;
}

function WorkLogPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selected, setSelected] = React.useState<string[]>([]);
  const [noTask, setNoTask] = React.useState(false);

  const currentQuery = useQuery({
    queryKey: ['work-log', 'current'],
    queryFn: () => api.getCurrentWorkSession(),
  });
  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.getMyTasks(),
  });

  const session = currentQuery.data ?? null;
  const tick = useMinuteTick(session !== null && !session.onBreak);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['work-log'] });
  };

  const clockInMutation = useMutation({
    mutationFn: () => api.clockIn({ taskIds: noTask ? [] : selected, noTask }),
    onSuccess: async () => {
      setSelected([]);
      await refresh();
    },
  });
  const breakStartMutation = useMutation({
    mutationFn: () => api.startWorkBreak(),
    onSuccess: refresh,
  });
  const breakEndMutation = useMutation({
    mutationFn: () => api.endWorkBreak(),
    onSuccess: refresh,
  });

  const openTasks: TaskSummary[] = React.useMemo(
    () => openTasksOf(tasksQuery.data),
    [tasksQuery.data],
  );

  const liveMinutes = React.useMemo(() => {
    if (!session) return 0;
    const start = new Date(session.clockInAt).getTime();
    const gross = Math.max(0, Math.floor((Date.now() - start) / 60_000));
    return Math.max(0, gross - session.breakMinutes);
  }, [session, tick]);

  const error = clockInMutation.error ?? breakStartMutation.error ?? breakEndMutation.error ?? null;

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-3xl space-y-8">
        <div>
          <h1 className="font-display text-4xl font-semibold md:text-5xl">Work log</h1>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-[hsl(var(--muted-foreground))]">
            Clock in against your tasks, take unpaid breaks, and declare where the time went.
          </p>
        </div>

        {error ? (
          <div className="rounded-lg border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-4 py-3 text-sm">
            {toMessage(error)}
          </div>
        ) : null}

        {session === null ? (
          <Card>
            <CardHeader>
              <CardTitle>Not clocked in</CardTitle>
              <CardDescription>
                Pick the tasks you are starting on, or choose “No task”. Time started with no task
                lands in your queue to map later.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                {openTasks.length === 0 ? (
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    No tasks assigned to you right now.
                  </p>
                ) : (
                  openTasks.map((t) => (
                    <label
                      key={t.id}
                      className={
                        'flex items-center gap-3 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm ' +
                        (noTask
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer hover:bg-[hsl(var(--accent))]')
                      }
                    >
                      <input
                        type="checkbox"
                        disabled={noTask}
                        checked={!noTask && selected.includes(t.id)}
                        onChange={(e) =>
                          setSelected((prev) =>
                            e.target.checked ? [...prev, t.id] : prev.filter((id) => id !== t.id),
                          )
                        }
                      />
                      <span className="flex-1">{t.title}</span>
                      <Badge variant="muted">{t.statusLabel}</Badge>
                    </label>
                  ))
                )}
              </div>

              <label
                className={
                  'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ' +
                  (noTask
                    ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/[0.06]'
                    : 'border-dashed border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))]')
                }
              >
                <input
                  type="checkbox"
                  checked={noTask}
                  onChange={(e) => {
                    setNoTask(e.target.checked);
                    if (e.target.checked) setSelected([]);
                  }}
                />
                <span className="flex-1">
                  No task
                  <span className="block text-xs text-[hsl(var(--muted-foreground))]">
                    Start without a task — this time goes to your queue to map later.
                  </span>
                </span>
              </label>
              {!noTask && selected.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Pick at least one task, or choose “No task”, to clock in.
                </p>
              ) : null}
              <Button
                onClick={() => clockInMutation.mutate()}
                disabled={clockInMutation.isPending || (!noTask && selected.length === 0)}
                className="w-full"
              >
                {clockInMutation.isPending ? 'Clocking in…' : 'Clock in'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{session.onBreak ? 'On break' : 'Clocked in'}</CardTitle>
              <CardDescription>
                Started {new Date(session.clockInAt).toLocaleTimeString()} · business date{' '}
                {session.businessDate}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-xl border border-[hsl(var(--border))] p-6 text-center">
                <div className="font-display text-4xl font-semibold tabular-nums">
                  {formatDuration(liveMinutes)}
                </div>
                <p className="mt-1 text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  worked so far
                  {session.breakMinutes > 0
                    ? ` · ${formatDuration(session.breakMinutes)} on break`
                    : ''}
                </p>
              </div>

              {session.splits.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Selected at clock-in
                  </p>
                  {session.splits.map((s) => (
                    <div key={s.id} className="text-sm">
                      {s.taskTitle ?? 'Unmapped time'}
                      {s.projectName ? (
                        <span className="text-[hsl(var(--muted-foreground))]">
                          {' '}
                          · {s.projectName}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="flex gap-2">
                {session.onBreak ? (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => breakEndMutation.mutate()}
                    disabled={breakEndMutation.isPending}
                  >
                    End break
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => breakStartMutation.mutate()}
                    disabled={breakStartMutation.isPending}
                  >
                    Start break
                  </Button>
                )}
                <Button
                  className="flex-1"
                  disabled={session.onBreak}
                  onClick={() => void navigate({ to: '/work-log/clock-out' })}
                >
                  Clock out
                </Button>
              </div>
              {session.onBreak ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  End your break before clocking out.
                </p>
              ) : null}
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
